/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * ResponseMappingEditor (#866) — inbound response-mapping authoring, the mirror
 * of the outbound ORTB body editor (#858) run in the other direction. Each row
 * pairs a **target** context field (store side — `VastAdDto`) with a **source**
 * expression that reads from the parsed response (retrieve side), both
 * autocompleted from per-protocol domain catalogs. Reuses the shared
 * `MappingControl` for the source expression (no parallel widget).
 *
 * Authored entries are custom OVERRIDES on top of the driver's default document:
 * optimizer-critical fields (bid, duration, adDomain, iabCategory, adId) are
 * extracted by the defaults out of the box; map here only to override a
 * non-standard response format. Follows `docs/MAPPING_EDITOR_UX_GUIDANCE.md`.
 */

import { rowSeverity } from './mapping-issues';
import React from 'react';
import { useExpressionNameLookup } from '../../../hooks/useExpressionLibrary';
import { Box, Button, HStack, NativeSelect, Text, Textarea, VStack } from '@chakra-ui/react';
import { MappingAutocompleteInput } from '../inputs/MappingAutocompleteInput';
import { rowsAfterApply, rowsAfterCancel, type RowEditSnapshot } from './row-edit-snapshot';
import { originMarks } from './row-origin-marks';
import { MappingControl, type MappingSurfaceCapabilities } from './MappingControl';
import { MappingRow, MappingRowList } from './MappingRow';
import { rowAnchorId, useFindingReview, useRowOpenRequest, type RowOpenRequest } from './row-open-request';
import { ValidationSummary } from './ValidationSummary';
import { summarizeMacroExpression } from './expression-summary';
import { leafAddressesChangedExpression, leafDefault } from './mapping-value-round-trip';
import type { RowMappingKind } from './parameter-editor.adapters';
import type { MappingLeaf, ScalarType } from './ortb-body-editor.adapters';
import {
  OPTIMIZER_CRITICAL_TARGETS,
  AD_TARGET_CATALOG,
  adTargetField,
  canonicalTarget,
  recommendedSourceFor,
  DRIVER_SUPPLIED_TARGETS,
  RESPONSE_INTRO,
  rowCardinalityMark,
  sourceSuggestions,
  targetGrain,
  TARGET_LIFETIME_CAVEAT,
  TARGET_LIFETIME_CAVEAT_DETAIL,
  targetSuggestions,
  validateResponseMapping,
  type ResponseSourceDomain,
} from './response-field-catalog';
import { ProvenanceLegend } from './ProvenanceLegend';
import { AutomaticPanel, AutomaticRow } from './AutomaticPanel';
import { canonicalizeInboundTarget, type DestinationFieldType } from '@destination-field';
import { diffMappingEntries, parseSampleResponse } from '../../../utils/mapping-suggest';
import { PayloadViewer } from '../PayloadViewer';
import { detectPayloadLanguage } from '../payload-language';
import type { MacroExpression } from '../../../types/decorator-expression';
import type { MappingDocument, MappingEntry, MappingScalarType } from '../../../types/mapping';
import type { SavedLibraryEntry } from './DecoratorExpressionEditor';
import type { ResponseMappingPreview } from '../../../types/response-mapping-preview';
import { describeRowResolution, describeSuppliedValue } from './response-resolution';

export interface ResponseMappingEditorProps {
  value?: MappingDocument | null;
  sourceDomain: ResponseSourceDomain;
  /**
   * The source's protocol version (VAST 2.0..4.2 / OpenRTB 2.5|2.6). Makes source
   * suggestions + validation version-aware — a path that only exists from a later
   * version (ORTB `response.dur` on 2.5, VAST `<Pricing>` on 2.0) is hidden from
   * autocomplete and, if already authored, flagged as won't-populate.
   */
  protocolVersion?: string;
  /** When embedded in a CollapsibleEditorSection the section owns the intro copy. */
  embedded?: boolean;
  /** Read-only preview: description + rows only, no toolbar/validation/coverage/add. */
  readOnly?: boolean;
  onChange: (doc: MappingDocument) => void;
  /**
   * Promote an extraction expression into the shared library — **required, `null` to decline**.
   *
   * Absent here until 2026-08-27. An inbound extraction is as reusable as an outbound mapping —
   * *price is at `seatbid.0.bid.0.price` on this exchange family* is exactly a library entry — and
   * this surface simply never had the prop. Found by making every `MappingControl` prop required,
   * not by anyone noticing (`#1134`).
   */
  onSaveExpressionToLibrary:
    | ((name: string, description: string, expr: MacroExpression) => Promise<SavedLibraryEntry>)
    | null;
  /**
   * "Map from response" (#866, #992). `null` on a surface that cannot author a
   * mapping — the read-only preview.
   *
   * **The surface owns the policy; this editor owns only the presentation.** The
   * editor used to run its own driver call, which made it a second way to do what
   * the page's Test button already did: same endpoint, same payload, two real
   * auctions — and the editor's call passed no test `context`, so the mapping was
   * derived from a response to a *different request* than the one the operator
   * validated against.
   *
   * So `onMapFrom` is implemented by the page. It returns the response to map
   * from, running a test only if there is not already a capture, and is free to
   * rearrange the page first (the detail page collapses the sections above so the
   * test panel and the mapping sit on one screen).
   */
  mapFrom: {
    /**
     * Whether a capture already exists. Presentation only — it decides whether the
     * button offers to reuse a response or to go and get one, so the operator is
     * never surprised by an unannounced call to the exchange.
     */
    hasCapture: boolean;
    /** Resolves with the response body to map from. Throwing surfaces inline. */
    run: () => Promise<string>;
  } | null;
  /**
   * **What the mapping on screen picks out of the sample on screen, per ad** (`#1144`) — `null` on a
   * surface that cannot run one. The read-only preview runs one since `#1488`, against the last test's
   * response.
   *
   * The surface owns it for the reason it owns `mapFrom`: it holds the rest of the source, and the
   * preview must run against the source **as it is on screen**, draft included (`#1002`). The editor
   * only decides *when*: whenever a mapped sample is on screen, again after every change to the
   * mapping, and never from inside a change handler.
   */
  previewSample:
    | ((sample: string, doc: MappingDocument, targets: readonly string[]) => Promise<ResponseMappingPreview>)
    | null;
  /**
   * **The response the surface's last test returned**, or `null` where there is none (`#1488`).
   *
   * An operator who has just tested expects the rows to show what they found in that response, in the
   * read view as well as the editor, without opening the editor and pressing *Map from response*. It is
   * **shown, never stored and never mapped**: the rows are resolved against it through `previewSample`,
   * and nothing is added to the mapping. A sample the operator pasted or mapped from takes its place
   * while it is on screen. Required and nullable, so a surface with a test cannot forget to pass it
   * (`#1134`).
   */
  latestResponse: string | null;
  /**
   * **The row a finding asked this editor to open or reveal** (`#1305`), or `null` where nothing on
   * the surface can point at a row — a read-only preview, or a caller with no findings panel.
   *
   * Required and nullable, the treatment every prop on a shared control carries since `#1134`.
   */
  openRow: RowOpenRequest | null;
}

const PATH_PLACEHOLDER: Record<ResponseSourceDomain, string> = {
  'ortb-bid-response': 'response.price',
  'vast-ad-response': 'response.raw',
};

type SampleOrigin = 'fetch' | 'paste';

/**
 * The sample response on screen. `applied` distinguishes "typed but not yet
 * mapped" (the Parse & map action is pending) from "already mapped" (the box is
 * a read-only record of what the suggestions came from).
 */
type SampleState = { text: string; origin: SampleOrigin; applied: boolean } | null;

/** The last bulk mutation — what it touched, and the list it replaced. */
interface BulkChange {
  label: string;
  added: string[];
  updated: string[];
  previous: MappingEntry[];
  /** The sample only restated mappings already configured — nothing to undo. */
  noop?: boolean;
}

function emptyDoc(sourceDomain: ResponseSourceDomain): MappingDocument {
  return { direction: 'inbound', sourceDomain, targetDomain: 'session-pod-context', entries: [] };
}

/** entry.source (MacroExpression) → MappingLeaf for MappingControl. */
function entryToLeaf(entry: MappingEntry): MappingLeaf {
  const path = entry.target as (string | number)[];
  if (entry.source.type === 'static') {
    return { path, kind: 'static', staticValue: entry.source.value, asType: entry.asType as ScalarType | undefined };
  }
  return { path, kind: 'expression', expression: entry.source, asType: entry.asType as ScalarType | undefined };
}

/** MappingLeaf (from MappingControl) → entry.source + asType. */
function leafToSource(leaf: MappingLeaf): { source: MacroExpression; asType?: MappingScalarType } {
  const asType = leaf.asType as MappingScalarType | undefined;
  if (leaf.kind === 'static') {
    return { source: { type: 'static', value: leaf.staticValue == null ? '' : String(leaf.staticValue) }, asType };
  }
  if (leaf.kind === 'expression' && leaf.expression) {
    return { source: leaf.expression, asType };
  }
  // 'macro' tab is hidden for inbound; fall back to an empty path expression.
  return { source: { type: 'calculated', path: '' }, asType };
}

/** The type a target declares, where a static on this surface can hold it — a scalar. */
function scalarTargetType(entry: MappingEntry): DestinationFieldType | null {
  const type = adTargetField(entry.target.join('.'))?.valueType;
  return type === 'string' || type === 'number' || type === 'boolean' ? type : null;
}

/** Classify a response source for the shared row chip's tint. */
function sourceKind(entry: MappingEntry): RowMappingKind {
  const s = entry.source;
  if (s.type === 'static') return s.value === '' || s.value == null ? 'empty' : 'static';
  if (s.type === 'calculated') return s.path ? (s.regex ? 'expression' : 'path') : 'empty';
  return 'expression';
}

export const ResponseMappingEditor: React.FC<ResponseMappingEditorProps> = ({
  value,
  sourceDomain,
  protocolVersion,
  embedded = false,
  readOnly = false,
  latestResponse,
  onChange,
  onSaveExpressionToLibrary,
  mapFrom,
  previewSample,
  openRow,
}) => {
  // The library once, above the rows — see `useExpressionNameLookup`. Without it a row that
  // addresses a shared expression reads *shared expression* while the row next to it on
  // another surface names one, which is the drift a shared summary exists to prevent.
  const expressionNameFor = useExpressionNameLookup();
  const responseLabel = sourceDomain === 'vast-ad-response' ? 'VAST response' : 'bid response';
  const doc = value ?? emptyDoc(sourceDomain);
  const entries = doc.entries;

  const issues = React.useMemo(() => validateResponseMapping(doc, protocolVersion), [doc, protocolVersion]);

  const [editing, setEditing] = React.useState<number | null>(null);

  /**
   * **How this editor answers _which row is that_** — a response mapping is keyed by its **index**
   * and named by its target, which is what `validateResponseMapping` puts in a finding's `ref`. The
   * two are not the same handle, and that gap is why the declaration is a lookup each editor owns
   * rather than the page reaching into their state.
   */
  const resolveEntryRow = (ref: string): number | null => {
    const wanted = ref.trim();
    const idx = entries.findIndex((e) => (e.target.join('.') || '(unnamed)') === wanted);
    return idx === -1 ? null : idx;
  };
  // `openEditor`, not `setEditing` — the snapshot is what Cancel restores (`#1262`).
  const openExistingEntry = (idx: number) => openEditor(idx);
  const findingReview = useFindingReview(
    'responseMapping',
    resolveEntryRow,
    (ref) => resolveEntryRow(ref) !== null,
    openExistingEntry,
  );
  useRowOpenRequest<number>('responseMapping', openRow, resolveEntryRow, openExistingEntry);
  /** The entry as it was when its editor opened — restored by Cancel. */
  const [editSnapshot, setEditSnapshot] = React.useState<RowEditSnapshot<MappingEntry> | null>(null);
  const [sample, setSample] = React.useState<SampleState>(null);
  const [sampleError, setSampleError] = React.useState<string | null>(null);
  // Smart-quote-normalized sample, set only when normalizing it would actually parse.
  // A pasted sample nearly always came via a doc, notes app or chat client, any of
  // which may have rewritten the quotes — naming the cause without offering the repair
  // leaves the operator to hand-fix dozens of them (#943 covered the ORTB body paste
  // but not this surface).
  const [sampleQuoteFix, setSampleQuoteFix] = React.useState<string | null>(null);
  const [lastChange, setLastChange] = React.useState<BulkChange | null>(null);
  const [fetching, setFetching] = React.useState(false);

  const changedTargets = React.useMemo(() => {
    const map = new Map<string, 'added' | 'updated'>();
    for (const t of lastChange?.added ?? []) map.set(t, 'added');
    for (const t of lastChange?.updated ?? []) map.set(t, 'updated');
    return map;
  }, [lastChange]);

  /**
   * **Canonicalised, because a target is one field however it was spelled** (`X49`, `#1232`).
   *
   * Every stored document authored before `X49` holds a bare `bid`, and everything downstream —
   * *is this already mapped*, *which optimizer-critical fields are missing*, *what does the driver
   * already supply* — is asking about the **field**, not about the spelling. Comparing raw would
   * have offered `ad.bid` in the picker beside an existing `bid` row and reported the critical set
   * unmapped on every pre-existing source.
   */
  const presentTargets = React.useMemo(() => entries.map((e) => canonicalTarget(e.target.join('.'))), [entries]);
  /**
   * What the driver fills without being asked, minus anything already mapped (`#1233`).
   *
   * Stated as a panel rather than as rows in the warnings list: seven `info` entries on every source
   * would set `worstLevel` to `info` permanently and bury the warnings that need action — the same
   * failure a provenance system dies of when it chips every value.
   */
  const driverSupplied = React.useMemo(
    () =>
      Object.entries(DRIVER_SUPPLIED_TARGETS[sourceDomain] ?? {}).filter(
        ([target]) => !presentTargets.includes(canonicalTarget(target)),
      ),
    [sourceDomain, presentTargets],
  );

  /**
   * **The sample, resolved** (`#1144`). Run when a mapped sample is on screen and again after every
   * change to the mapping — from an effect, never a change handler, so it reads the mapping as it now
   * is (`#999`). Between a change and its answer every mark is **stale**, and a stale mark never shows a
   * value: a value from the previous mapping is a claim about something no longer on screen (`#1002`).
   */
  const [preview, setPreview] = React.useState<ResponseMappingPreview | null>(null);
  const [previewStale, setPreviewStale] = React.useState(false);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  const previewRun = React.useRef(0);
  /** The last test's response, shown against the rows while no sample of the operator's is (`#1488`). */
  const shownTest = sample ? null : latestResponse?.trim() ? latestResponse : null;
  const previewText = sample?.applied ? sample.text : shownTest;
  const previewTargets = React.useMemo(
    () => Array.from(new Set([...presentTargets, ...Object.keys(DRIVER_SUPPLIED_TARGETS[sourceDomain] ?? {})])),
    [presentTargets, sourceDomain],
  );
  const docKey = React.useMemo(() => JSON.stringify(doc), [doc]);
  React.useEffect(() => {
    const run = ++previewRun.current;
    if (!previewSample || !previewText) {
      setPreview(null);
      setPreviewStale(false);
      setPreviewError(null);
      return;
    }
    setPreviewStale(true);
    const timer = setTimeout(() => {
      previewSample(previewText, JSON.parse(docKey) as MappingDocument, previewTargets)
        .then((result) => {
          if (run !== previewRun.current) return;
          setPreview(result);
          setPreviewError(result.parsed ? null : (result.error ?? 'The sample could not be read.'));
        })
        .catch((err: unknown) => {
          if (run !== previewRun.current) return;
          setPreview(null);
          setPreviewError(err instanceof Error ? err.message : 'Could not read the sample.');
        })
        .finally(() => {
          if (run === previewRun.current) setPreviewStale(false);
        });
    }, 300);
    return () => clearTimeout(timer);
    // `previewSample` is a surface callback that may be rebuilt every render; the inputs that change
    // the answer are the sample, the mapping and the targets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewText, docKey, previewTargets]);

  /** How many ads the rows answer for (`#1144`), stated because a row shows one value per ad. */
  const adCountSentence =
    preview?.parsed && !previewStale
      ? preview.ads.length === 0
        ? '. It holds no ads, so there is nothing to show beside each row.'
        : `. ${preview.ads.length} ad${preview.ads.length === 1 ? '' : 's'}: each row shows ${
            preview.ads.length === 1 ? 'what it found' : 'what it found in each, in order'
          }.`
      : '';

  const emit = (next: MappingEntry[]) => onChange({ ...doc, sourceDomain, entries: next });

  /**
   * Any hand edit invalidates the pending undo snapshot — reverting after it
   * would silently throw the operator's own change away — and clears the
   * added/updated highlights, which no longer describe the current list.
   */
  const clearLastChange = () => setLastChange(null);

  const addEntry = (target: string) => {
    const t = target.trim();
    if (!t) return;
    /**
     * Pre-filled from the standard expression where one exists — guidance §5, and it is
     * what makes this the *override* path rather than a blank slate (#1013).
     *
     * An operator adding `bid` on a source whose response is non-standard wants to start
     * from what the driver does and adjust it; starting from an empty path means retyping
     * the standard one from memory before they can change it. Add-recommended used to be
     * the only pre-filling path, and it was pre-filling things that needed no filling.
     */
    const rec = recommendedSourceFor(sourceDomain, t);
    // **A target is a path, and it is stored namespaced** (`X49`). This built `[t]` — one segment —
    // so `ad.bid` would have been stored as a single field literally called `ad.bid`, and a nested
    // target was unauthorable from the picker at all.
    const path = canonicalizeInboundTarget(t.split('.').filter(Boolean));
    const next = [
      ...entries,
      rec
        ? { target: path, source: rec.source, asType: rec.asType, provenance: { kind: 'template' as const } }
        : { target: path, source: { type: 'calculated', path: '' } as MacroExpression },
    ];
    clearLastChange();
    emit(next);
    setEditSnapshot({ row: next[next.length - 1], origin: 'created' });
    setEditing(next.length - 1);
  };

  const updateEntryTarget = (idx: number, target: string) => {
    const next = entries.map((e, i) => (i === idx ? { ...e, target: target ? target.split('.') : [] } : e));
    clearLastChange();
    emit(next);
  };

  const updateEntrySource = (idx: number, leaf: MappingLeaf) => {
    const { source, asType } = leafToSource(leaf);
    const next = entries.map((e, i) => (i === idx ? { ...e, source, asType } : e));
    clearLastChange();
    emit(next);
  };

  /**
   * **Accept the system default, live on this surface** (`X33a`, `#1267`, ratified by Todd
   * 2026-09-10): *"it's either 'mine' (unchecked) or the system's (checked) — there is no 'suppress'
   * action, so it meets my test."*
   *
   * The system's value here is **what the driver extracted**, so the control is offered on exactly
   * the targets the driver actually fills and is `null` on the rest — a checkbox about a fall-back
   * that does not exist would be a claim, not a capability. That is the same rule the parameter
   * editor follows (*`null` where nothing is displaced, which is most rows*), asked of a different
   * catalogue.
   *
   * `from` names **which** value is being declined rather than describing the mechanism; the full
   * sentence stays in the panel above, where there is room for it and where it is stated once.
   */
  const systemDefaultForTarget = (
    entry: MappingEntry,
    idx: number,
  ): MappingSurfaceCapabilities['acceptsSystemDefault'] => {
    const supplied = DRIVER_SUPPLIED_TARGETS[sourceDomain]?.[canonicalTarget(entry.target.join('.'))];
    if (!supplied) return null;
    return {
      from: supplied.from,
      value: entry.acceptSystemDefault !== false,
      // Stored only when declined, so an untouched document stays untouched — the spelling both
      // other surfaces already use for this flag.
      onChange: (next: boolean) => {
        const patch = next ? { acceptSystemDefault: undefined } : { acceptSystemDefault: false as const };
        clearLastChange();
        emit(entries.map((e, i) => (i === idx ? { ...e, ...patch } : e)));
      },
    };
  };

  const removeEntry = (idx: number) => {
    clearLastChange();
    emit(entries.filter((_, i) => i !== idx));
    setEditing(null);
    setEditSnapshot(null);
  };

  const openEditor = (idx: number) => {
    const row = entries[idx];
    setEditSnapshot(row ? { row, origin: 'existing' } : null);
    setEditing(idx);
  };

  /** Apply: keep what was typed, accept a detected guess, and close. */
  const applyEdit = () => {
    const idx = editing;
    const held = editSnapshot;
    setEditing(null);
    setEditSnapshot(null);
    // Open and Apply accepts the entry (`#1001`) — `rowsAfterApply`. A parsed response entry is found
    // by name in a fixed table and carries no guess today; the rule is here for the first that does.
    const confirmed = rowsAfterApply(entries, (_, i) => i === idx, held);
    if (confirmed !== entries) emit(confirmed);
  };

  /**
   * Cancel: put the row back to what it was when the editor opened — or take it away, where the
   * gesture that opened the editor is what created it (`#1262`).
   */
  const cancelEdit = () => {
    const snapshot = editSnapshot;
    const idx = editing;
    setEditing(null);
    setEditSnapshot(null);
    if (!snapshot || idx == null) return;
    clearLastChange();
    emit(rowsAfterCancel(entries, snapshot, (_, i) => i === idx));
  };

  /** Apply a bulk change, recording what it did so it can be shown and undone. */
  const applyBulk = (next: MappingEntry[], label: string, touched: MappingEntry[]) => {
    const before = new Set(presentTargets);
    const added = touched.map((e) => e.target.join('.')).filter((t) => !before.has(t));
    const updated = touched.map((e) => e.target.join('.')).filter((t) => before.has(t));
    setLastChange({ label, added, updated, previous: entries });
    emit(next);
  };

  const undoLastChange = () => {
    if (!lastChange) return;
    emit(lastChange.previous);
    setLastChange(null);
    // The sample stays on screen, now un-applied, so it can be re-mapped.
    setSample((prev) => (prev ? { ...prev, applied: false } : prev));
  };

  /**
   * Offer only what is genuinely absent — **not** what the driver already supplies (#1013).
   *
   * Every target `recommendedSourceFor` can return is, by construction, one the driver's
   * own default document already applies: `ORTB_STANDARD_SOURCE`'s keys are byte-identical
   * to `defaultOrtbResponseMapping`'s targets, and the VAST branch to
   * `defaultVastResponseMapping`'s. So this button added rows that changed nothing, and
   * the editor then counted them as "N overridden" — overrides that override nothing with
   * the same value.
   *
   * With `driverSupplied` in the filter the candidate set is empty today and the button
   * hides, which is the honest outcome. It is left in rather than deleted so that a target
   * which stops being a driver default reappears here automatically — the sets are asserted
   * against the backend by `response-field-catalog.driver-defaults.test.ts`, so the day
   * they diverge is a deliberate day.
   */
  const addRecommended = () => {
    const missing = OPTIMIZER_CRITICAL_TARGETS.filter(
      (t) => !presentTargets.includes(t) && !(t in (DRIVER_SUPPLIED_TARGETS[sourceDomain] ?? {})),
    );
    if (missing.length === 0) return;
    // Seed each missing optimizer target with its STANDARD working source
    // expression (ORTB path / VAST regex) — mirrors the driver default document.
    const stdEntries: MappingEntry[] = [];
    for (const target of missing) {
      const rec = recommendedSourceFor(sourceDomain, target);
      // `template` (R4/R4b): the catalog composed this source expression for the
      // protocol, not for this source. The click authored the inclusion.
      if (rec) {
        stdEntries.push({ target: [target], source: rec.source, asType: rec.asType, provenance: { kind: 'template' } });
      }
    }
    if (stdEntries.length === 0) return;
    applyBulk(
      [...entries, ...stdEntries],
      `Added ${stdEntries.length} recommended mapping${stdEntries.length === 1 ? '' : 's'}.`,
      stdEntries,
    );
  };

  /**
   * Parse the current sample (pasted or fetched) and merge the suggested entries
   * by target — a suggestion replaces a same-target entry, everything else stays.
   * The sample stays on screen afterwards so the mapping can be read against it.
   */
  const applySample = (text: string, origin: SampleOrigin): boolean => {
    let result = parseSampleResponse(text, sourceDomain);
    if (result.error) {
      setSampleError(result.error);
      setSampleQuoteFix(result.quoteFix ?? null);
      return false;
    }
    setSampleQuoteFix(null);

    /**
     * Drop suggestions the driver already applies (#1013).
     *
     * `parseSampleResponse` proposes a mapping for every standard target it finds at its
     * standard location — which is, by construction, exactly what the default document
     * already does. So "Map from response" on a normal VAST ad added `bid` and `adDomain`
     * as `detected`, identical to what the driver extracts anyway, and the evidence panel
     * directly above had *already shown both values populated*.
     *
     * Compared by **source expression**, not merely by target: the day the parser learns
     * to find a price somewhere non-standard, that mapping is a real override and must
     * survive this filter. Only a proposal that would change nothing is dropped.
     */
    const supplied = DRIVER_SUPPLIED_TARGETS[sourceDomain] ?? {};
    const meaningful = result.entries.filter((e) => {
      // Canonicalised (`X49`): `DRIVER_SUPPLIED_TARGETS` is keyed on the stored spelling and a
      // parsed suggestion may carry either, so a raw lookup would silently stop filtering — which
      // is `#1013` returning through a rename.
      const target = canonicalTarget(e.target.join('.'));
      if (!(target in supplied)) return true;
      const std = recommendedSourceFor(sourceDomain, target);
      return !std || JSON.stringify(std.source) !== JSON.stringify(e.source);
    });
    const alreadySupplied = result.entries.length - meaningful.length;

    if (meaningful.length === 0) {
      // Not an error, and not silence. The operator asked a question and the answer is
      // "nothing needed" — which is worth saying, because the alternative reading of a
      // no-op is that the button is broken.
      setLastChange({
        label:
          `The ${origin === 'fetch' ? 'fetched response' : 'pasted sample'} is standard — ` +
          `all ${alreadySupplied} field${alreadySupplied === 1 ? '' : 's'} in it ` +
          `${alreadySupplied === 1 ? 'is' : 'are'} already extracted by the driver. Nothing to map.`,
        added: [],
        updated: [],
        previous: entries,
        noop: true,
      });
      setSampleError(null);
      setSample({ text, origin, applied: true });
      return true;
    }
    result = { ...result, entries: meaningful };

    // Only count a suggestion as a change when it actually differs from what is
    // already configured — re-parsing the same response must not claim edits.
    const diff = diffMappingEntries(entries, result.entries);
    const changed = [...diff.added, ...diff.updated];
    const from = origin === 'fetch' ? 'fetched response' : 'pasted sample';

    if (changed.length === 0) {
      setLastChange({
        label: `The ${from} matches the ${result.entries.length} mapping${
          result.entries.length === 1 ? '' : 's'
        } already configured — nothing changed.`,
        added: [],
        updated: [],
        previous: entries,
        noop: true,
      });
    } else {
      const byTarget = new Map(entries.map((e) => [e.target.join('.'), e]));
      for (const e of result.entries) byTarget.set(e.target.join('.'), e);
      applyBulk(
        Array.from(byTarget.values()),
        `Mapped ${changed.length} field${changed.length === 1 ? '' : 's'} from the ${from}.`,
        changed,
      );
    }
    setSampleError(null);
    setSample({ text, origin, applied: true });
    return true;
  };

  /**
   * Auto-fetch (#866): run a real driver call against the current config (#872)
   * and map from what actually came back. The response stays in the sample box
   * so the operator can see what the suggestions were derived from.
   */
  const fetchSample = async () => {
    if (!mapFrom) return;
    setFetching(true);
    setSampleError(null);
    try {
      const text = await mapFrom.run();
      if (!text || !text.trim()) {
        setSample(null);
        setSampleError('The demand source returned an empty response — nothing to map.');
        return;
      }
      if (!applySample(text, 'fetch')) setSample({ text, origin: 'fetch', applied: false });
    } catch (err: unknown) {
      setSample(null);
      setSampleError(err instanceof Error ? err.message : 'Could not fetch a sample response.');
    } finally {
      setFetching(false);
    }
  };

  return (
    <VStack align="stretch" gap={4}>
      <HStack justify={embedded ? 'flex-end' : 'space-between'} flexWrap="wrap" gap={2}>
        {!embedded && (
          <Text fontSize="xs" color="fg.muted" maxW="70%">
            {/* **One sentence, and the fan-out is its first clause** (`X56a`, `#1296`, collapsed by
                Todd on reading it). It also retires *into a session/ad context field*, which named a
                vocabulary that is not reachable here — the target has been the ad since `X49`. */}
            {RESPONSE_INTRO}
          </Text>
        )}
        {!readOnly && (
          <HStack gap={2}>
            {mapFrom && (
              <Button
                size="xs"
                variant="outline"
                borderColor="accent.solid"
                color="accent.solid"
                _hover={{ bg: 'accent.subtle' }}
                onClick={fetchSample}
                disabled={fetching}
                title={
                  mapFrom.hasCapture
                    ? 'Map from the response the last test returned'
                    : 'Run a test against this demand source and map from what it returns'
                }
              >
                {fetching
                  ? mapFrom.hasCapture
                    ? 'Mapping…'
                    : 'Testing…'
                  : mapFrom.hasCapture
                    ? 'Map from response'
                    : 'Test & map from response'}
              </Button>
            )}
            <Button
              size="xs"
              variant="outline"
              borderColor="border.default"
              color="fg.muted"
              onClick={() => {
                setSampleError(null);
                setSample({ text: '', origin: 'paste', applied: false });
              }}
            >
              Paste sample…
            </Button>
            <Button size="xs" bg="accent.solid" color="white" _hover={{ bg: 'accent.ink' }} onClick={addRecommended}>
              + Add recommended
            </Button>
          </HStack>
        )}
      </HStack>

      {/* **The coverage pills are gone** (`#1259`, Todd 2026-09-08). Five chips named the
          optimizer-critical targets and ticked the ones a row overrode — *"duplicative: the
          extracted auto is the place to show the user and provide the override capability with
          `edit`"*. Every one of the five is a driver-supplied target, so each was stated twice on one
          screen: as a chip here and as a row in **Extracted automatically** below, which also says
          *where* the value comes from and offers the way to take it over. Two controls over one
          slot, and each was correct — which is why nothing but counting them finds it.

          What the chips carried that the panel did not is the *optimizer* half, and that is now on
          the rows themselves rather than on a second list of names. */}

      {!readOnly && <ValidationSummary issues={issues} onFix={null} review={findingReview} />}

      {sample && (
        <Box p={3} bg="bg.subtle" borderWidth="1px" borderColor="border.default" borderRadius="8px">
          <HStack justify="space-between" mb={2} gap={2}>
            <Text fontSize="xs" fontWeight="600" color="fg.default">
              {sample.origin === 'fetch' ? 'Fetched response' : 'Sample response'}
              {sample.applied && (
                <Text as="span" fontWeight="400" color="fg.muted">
                  {' '}
                  — mapped
                  {/* How many ads the rows below answer for (`#1144`). Stated because a row shows one
                      value per ad, and three values beside `ad.bid` only read as three ads if the
                      surface has said so. */}
                  {adCountSentence}
                </Text>
              )}
            </Text>
            <Button size="xs" variant="ghost" color="fg.muted" onClick={() => setSample(null)}>
              Close
            </Button>
          </HStack>

          {sample.applied ? (
            // Already mapped: the sample is a read-only record of where the
            // suggestions came from, so there is no pending action to offer.
            <PayloadViewer value={sample.text} language={detectPayloadLanguage(sample.text)} maxHeight="240px" />
          ) : (
            <>
              <Textarea
                size="sm"
                fontFamily="mono"
                rows={6}
                value={sample.text}
                onChange={(e) => setSample({ ...sample, text: e.target.value })}
                placeholder={
                  sourceDomain === 'ortb-bid-response'
                    ? 'Paste a captured ORTB bid response (JSON)…'
                    : 'Paste a captured VAST response (XML)…'
                }
                bg="bg.card"
              />
              <Button
                size="xs"
                mt={2}
                bg="accent.solid"
                color="white"
                _hover={{ bg: 'accent.ink' }}
                onClick={() => applySample(sample.text, sample.origin)}
              >
                {sample.origin === 'fetch' ? 'Map from this response' : 'Parse & map'}
              </Button>
            </>
          )}

          {previewError && !sampleError && (
            <Text mt={2} fontSize="xs" color="warning.solid">
              The rows cannot show what they find in this sample: {previewError}
            </Text>
          )}

          {sampleError && (
            <HStack mt={2} gap={2} align="flex-start">
              <Text fontSize="xs" color="error.solid">
                {sampleError}
              </Text>
              {sampleQuoteFix && (
                <Button
                  size="xs"
                  variant="outline"
                  flexShrink={0}
                  onClick={() => {
                    const fixed = sampleQuoteFix;
                    // Show the corrected sample, then map from it — the operator sees
                    // what was changed rather than a silent repair.
                    setSample({ text: fixed, origin: sample.origin, applied: sample.applied });
                    setSampleError(null);
                    setSampleQuoteFix(null);
                    applySample(fixed, sample.origin);
                  }}
                >
                  Fix quotes &amp; parse
                </Button>
              )}
            </HStack>
          )}
        </Box>
      )}

      {shownTest && (
        <Box p={3} bg="bg.subtle" borderWidth="1px" borderColor="border.default" borderRadius="8px">
          <Text fontSize="xs" fontWeight="600" color="fg.default">
            Latest test response
            <Text as="span" fontWeight="400" color="fg.muted">
              {/* Shown, not mapped (`#1488`): the rows below read it, and the mapping is unchanged. */}
              {preview?.parsed && !previewStale ? adCountSentence : previewStale ? ' — reading it…' : ''}
            </Text>
          </Text>
          {previewError && (
            <Text mt={2} fontSize="xs" color="warning.solid">
              The rows cannot show what they find in this response: {previewError}
            </Text>
          )}
        </Box>
      )}

      {!sample && sampleError && (
        <Text fontSize="xs" color="error.solid">
          {sampleError}
        </Text>
      )}

      {/* What the last bulk change did, and the way back out of it. */}
      {lastChange && (
        <HStack
          gap={2}
          px={3}
          py={2}
          bg="accent.subtle"
          borderWidth="1px"
          borderColor="accent.solid"
          borderRadius="8px"
          flexWrap="wrap"
        >
          <Text fontSize="xs" color="accent.ink" flex="1">
            {lastChange.label}
            {(lastChange.added.length > 0 || lastChange.updated.length > 0) && (
              <Text as="span" color="fg.muted">
                {' '}
                {lastChange.added.length} added, {lastChange.updated.length} updated — highlighted below.
              </Text>
            )}
          </Text>
          {!lastChange.noop && (
            <Button
              size="xs"
              variant="outline"
              borderColor="accent.solid"
              color="accent.solid"
              onClick={undoLastChange}
            >
              Undo
            </Button>
          )}
          <Button size="xs" variant="ghost" color="fg.muted" onClick={() => setLastChange(null)}>
            Dismiss
          </Button>
        </HStack>
      )}

      {/* **Extracted automatically** — the inbound half of the panel the outbound surfaces have had
          since `#951 · R5` (`#1233`, `XS3` slice 6).
          It was a line of chips in the **wizard**, so the detail page said nothing at all and the two
          renderings of one concept could not be told apart. Both answer *do I need a row for this?*
          off the same kind of declaration, so they share the affordance — and it moves here, onto the
          mapping surface, because that is where the answer is acted on. */}
      {/* **Rendered in read-only too** (`#1233`, corrected 2026-09-04 by Todd on the detail page).
          This was gated `!readOnly` — copied from the guards around it without asking whether it
          applied — and it is the one place the guard is backwards. The panel is a **statement about
          what the runtime does**, not an authoring affordance, and a reader who cannot add a row is
          the person who most needs it: without it *there is no `bid` mapping here* reads as missing
          configuration rather than as the driver already doing it. The **`edit`** button is the
          affordance, and that is what read-only drops.

          It is also `#1233`'s own asymmetry surviving inside the fix that closed it — the outbound
          panel has always rendered in read-only, and only this one did not. */}
      <AutomaticPanel
        title="Extracted automatically"
        count={driverSupplied.length}
        action={readOnly ? null : 'edit to override'}
        caveat={
          <>
            The driver fills these with no mapping authored — for an OpenRTB source that includes the duration and media
            file read out of the VAST inside{' '}
            <Text as="span" fontFamily="mono">
              adm
            </Text>
            {readOnly ? '.' : '. Take one over only to read it from somewhere else.'}
          </>
        }
      >
        {driverSupplied.map(([target, supplied]) => (
          <AutomaticRow
            key={target}
            name={target}
            detail={supplied.detail}
            /* **Which of these reach ad selection** (`#1259`). The panel is the one place the whole
               driver-supplied set is on screen at once, and *"the user should see which of those
               fields are used by the optimizer"* is what the coverage pills above it were reaching
               for — said here, on the field it is true of, instead of as a second list of names. */
            mark={targetGrain(target).usedByOptimizer ?? null}
            resolution={describeSuppliedValue(target, preview, previewStale) ?? null}
            action={
              readOnly ? undefined : (
                <Button
                  size="xs"
                  variant="ghost"
                  color="accent.solid"
                  flexShrink={0}
                  onClick={() => addEntry(target)}
                  aria-label={`Override ${target}`}
                >
                  edit
                </Button>
              )
            }
          />
        ))}
      </AutomaticPanel>

      {/* Entries */}
      <VStack align="stretch" gap={2}>
        {entries.length === 0 && (
          <Text fontSize="sm" color="fg.muted" fontStyle="italic">
            No custom field mappings — standard optimizer fields are extracted from the {responseLabel} automatically.
            {!readOnly && ' Add a mapping or paste a sample to override where a value is read from.'}
          </Text>
        )}
        {entries.length > 0 && (
          /* **The header names what the control offers** (`#1232`, `#1045` on a label). It read
             *Session Context* over nine bid fields — a real concept with a real path vocabulary
             (`session.*`, declared in `context-roots.ts`), none of which is reachable here, so an
             operator who had learned the outbound surface's paths found none of them and had no way
             to tell a limit from a bug. The target is the **ad**, and since `X49` that is what the
             stored path says too. */
          <MappingRowList
            nameHeader="Ad Field"
            valueHeader={`Response Field (${responseLabel})`}
            /* **`X32` Q2 — how long does what I store here survive** (`#1248`), as a **scope**
               since 2026-09-10: one line under the heading, with the sentence it shortens on the
               hover and in the accessible tree rather than deleted. Still one claim per destination
               namespace, stated where the namespace is named rather than on each row. */
            nameCaveat={TARGET_LIFETIME_CAVEAT}
            nameCaveatDetail={TARGET_LIFETIME_CAVEAT_DETAIL}
          >
            {/* One legend per surface (R4), listing only the chips actually present. */}
            <ProvenanceLegend values={entries.map((e) => e.provenance)} />
            {entries.map((entry, idx) => {
              const change = changedTargets.get(entry.target.join('.'));
              return (
                <MappingRow
                  key={idx}
                  rootId={rowAnchorId('responseMapping', entry.target.join('.') || '(unnamed)')}
                  readOnly={readOnly}
                  editing={editing === idx}
                  highlighted={!!change}
                  summaryLabel={summarizeMacroExpression(entry.source, expressionNameFor)}
                  summaryKind={sourceKind(entry)}
                  onEdit={() => openEditor(idx)}
                  onApply={applyEdit}
                  onCancel={cancelEdit}
                  /* The mapping list is stored as one array, so a child states it whole and there is no
                     inherited *row* to suppress: removing an entry removes it (`#1126`). */
                  layerAction={{
                    kind: 'remove',
                    onAct: () => removeEntry(idx),
                    subject: entry.target.join('.') || 'mapping',
                  }}
                  editLabel={`Edit source for ${entry.target.join('.') || 'field'}`}
                  marks={{
                    // See `RequestParameterEditor` — the row's own findings, at the row. The key is
                    // the one `validateResponseMapping` raises a finding against.
                    severity: rowSeverity(issues, entry.target.join('.') || '(unnamed)'),
                    // Reads values out of a response; it authors no credential (`#1371`).
                    secret: null,
                    // No revert: a parsed entry is a standard target found by name, never a guess (`#1001`).
                    // `null`: the mapping is stored as one whole value, so a single entry has no template row to
                    // revert to — the whole mapping's revert is its heading's.
                    ...originMarks({ origin: entry.provenance }, null, null),
                    /* **The default standing behind the mapping** (`#913`/`#1269`). This surface was
                       the one of five that never asked for the mark, so a response mapping with a
                       default looked identical to one without — and on this surface that difference
                       decides whether a missed extraction leaves the ad unsellable or falls back. */
                    default: leafDefault(entryToLeaf(entry), expressionNameFor),
                    sharedChanged: leafAddressesChangedExpression(entryToLeaf(entry), expressionNameFor),
                    /* **`X32` Q3 — where can this be used** (`#1248`, flipped by `#1259`). Present
                       only when the optimizer selects on it: an author already assumes what they map
                       is stored on the ad and served, so *that* is the silent case and *this one
                       also chooses which ads run* is the fact worth a mark. */
                    grain: targetGrain(entry.target.join('.')).usedByOptimizer,
                    /* **`X56a` — is this one value or one per ad** (`#1296`). The surface says every
                       row runs once per returned ad; this is the row that does not vary with them,
                       which is the only thing left to mark once that sentence is on screen. */
                    cardinality: rowCardinalityMark(entry.source) ?? undefined,
                    /* **What this row found in the sample on screen, per ad** (`#1144`). */
                    resolution: describeRowResolution(canonicalTarget(entry.target.join('.')), preview, previewStale),
                    state: change ? (
                      <Text
                        fontSize="9px"
                        textTransform="uppercase"
                        fontWeight="700"
                        letterSpacing="0.04em"
                        color="accent.ink"
                        flexShrink={0}
                      >
                        {change}
                      </Text>
                    ) : undefined,
                  }}
                  name={entry.target.join('.')}
                  nameInput={
                    <MappingAutocompleteInput
                      size="sm"
                      bg="bg.subtle"
                      value={entry.target.join('.')}
                      getSuggestions={(q) =>
                        targetSuggestions(
                          q,
                          presentTargets.filter((t) => t !== canonicalTarget(entry.target.join('.'))),
                        )
                      }
                      placeholder="bid"
                      onChange={(t) => updateEntryTarget(idx, t)}
                    />
                  }
                >
                  <MappingControl
                    onSaveToLibrary={onSaveExpressionToLibrary}
                    /* **The target declares its type, and said so all along** (`FD4`, `4b`). This read
                       *"no per-target scalar catalog"* while `AD_TARGET_FIELDS` declared a type on 31
                       of 31. Scalars only: a static here is stored as text (`leafToSource`), so a list
                       or object target would be offered the list conversion and lose it on Apply. */
                    declaredType={scalarTargetType(entry)}
                    staticSuggestions={null}
                    leaf={entryToLeaf(entry)}
                    onChange={(leaf) => updateEntrySource(idx, leaf)}
                    pathSuggestions={(q) => sourceSuggestions(sourceDomain, q, protocolVersion)}
                    pathPlaceholder={PATH_PLACEHOLDER[sourceDomain]}
                    can={{
                      // A blank result writes nothing onto the ad, so the driver's own value stands (`coerceValue`).
                      emptyValue: 'left-off',
                      /* **Still refused, and the reason was false** (`#1236`, `XS3` slice 6).
                         It said a `{macro}` is a value *bidBrain* has and this side reads what the
                         exchange sent — but bidBrain's values are in this context and always were:
                         `applyResponseMapping` sets `pod`, `demandSource` and `session`. The real
                         limit is one line down: `leafToSource` has no macro branch, so a `{ip}`
                         typed here would be accepted and dropped on Apply.

                         Right answer, wrong sentence — and a refusal is the sentence least likely to
                         be checked, because it is read by whoever is being turned away. Storing one
                         is `X27`'s route (a macro is sugar over a path, canonicalised on the way in
                         the way an alias already is), not a resolution problem. */
                      acceptsMacro: {
                        reason: 'This surface stores a path or an expression, and has nowhere to keep a {macro} token.',
                      },
                      acceptsDerived: true,
                      /* Inbound. Nothing here goes on a wire, so the outbound rendering
                         question does not arise — `structured` is the honest class for a JSON
                         response and `sendAs` is declined outright. */
                      /* **Offered — the refusal was measurably false** (`#1236`, `XS3` slice 6).
                         It said *"library expressions read the session … none of them would resolve
                         here"*. `session.*` resolves here and does so through the same builder the
                         outbound surfaces use; the seeded `IP Extraction` (`session.user.ipAddress`)
                         would have worked all along. `libs/drivers/response-mapping.resolver.spec.ts`
                         asserts all four roots rather than arguing it.

                         What is left is the narrower claim `X18`/`XS5` computes per expression: one
                         reading `params.*` or `headers.*` genuinely will not resolve here, because
                         the request is over. That is a few entries rather than the whole library, so
                         offering it and refining later is the smaller error — the previous state
                         turned the operator away from a list that mostly works. */
                      offersLibrary: true,
                      /* Inbound: the request is over, so `params` and `headers` are genuinely out of
                         scope here and `response` is in. This is the surface `X18` was written for —
                         a library expression reading `session.*` resolves, one reading `params.*`
                         does not, and the picker marks the second rather than refusing the list. */
                      resolvesAgainst: 'responseMapping',
                      surface: 'structured',
                      /* Coerced through `MappingEntryDto.asType`, which this surface stores. */
                      storesValueType: true,
                      sendAs: null,
                      /* **Corrected 2026-09-10 (`#1267`): it *is* established, and the block below
                         said so in the same object.** `applyResponseMapping` skips an entry that is
                         suppressed, errored or unresolved, so what an extraction that finds nothing
                         does is *leave the driver's value alone*. Two declarations about one fact,
                         nine lines apart, disagreeing — `#1269`'s shape inside a capability set.

                         Still `null`, and now for a reason rather than for an absence: the behaviour
                         is fixed and Todd has ruled that declining it is not wanted, so there is no
                         answer for an operator to author here. */
                      whenUnresolved: null,
                      /* No macro accepted here, so nothing to store a default for. */
                      storesMacroDefault: null,
                      /* **`null`, ruled by Todd 2026-09-23** (`X46`, `#1366`). No row control, and no
                         refusal either: *"we auto send the 'required by optimizer' and show that note
                         on the lists."* The driver extracts the optimizer's fields itself, and the
                         lists already carry `usedByOptimizer`'s mark. **Used by the optimizer is not
                         required** — required is *this will not work without a value*, and coverage of
                         these in real responses is expected to be sparse — so the ad targets declare
                         `recommended` (`AD_TARGET_FIELDS`). A custom field promoted into `BidRefDto`
                         (`#871`) is marked the same way as today's fields. */
                      declaresImportance: null,
                      // `null`: a response mapping reads values out of a response; it authors no
                      // credential to store (`#1371`).
                      declaresSecret: null,
                      /* `null` — and `#1267` confirms it rather than deferring it. There is no
                         *is this on the wire* question on an inbound surface: nothing here is sent,
                         and *"removing is not the same type of problem in a response as it is on the
                         wire"* (Todd, 2026-09-10) is exactly why the vocabulary does not transfer. */
                      declaresOmission: null,
                      /* **`X33a`** — the driver's own extraction stands when a mapping resolves to
                         nothing, and it already does: `applyResponseMapping` skips an entry that is
                         suppressed, errored **or** unresolved, so what the driver extracted survives
                         untouched. `DRIVER_SUPPLIED_TARGETS` is the panel above, listing exactly
                         those values.

                         **Settled 2026-09-10 (`#1267`), and it is a decision rather than a wait.**
                         Todd: *"I'm not sure how important suppress is on response mapping at all.
                         Users who want to override should have the ability to set their default
                         value. 'Removing' is not the same type of problem in a response as it is on
                         the wire."* So the third outcome is not being built, and the sentence had to
                         change with the ruling: it read *"not authorable yet"*, which is **not yet
                         with nothing scheduled**, and this repo's own rule is that such a sentence is
                         *no*, written politely.

                         The replacement names the action instead, and the action is **on this row** —
                         `LeafDefaultControl` renders the Default on the value line beside this
                         checkbox, so the redirect points at a control the operator can already see.
                         *The check is not whether the product has the capability, it is whether this
                         control offers it.* */
                      acceptsSystemDefault: systemDefaultForTarget(entry, idx),
                    }}
                  />
                </MappingRow>
              );
            })}
          </MappingRowList>
        )}
      </VStack>

      {/* Add field */}
      {!readOnly && <AddTargetControl presentTargets={presentTargets} onAdd={addEntry} />}
    </VStack>
  );
};

/**
 * **The declared ad fields, and nothing typed** (`#1297`, Todd 2026-09-15).
 *
 * A free-text box sat beside this select, first as *"or type a custom context field…"* and briefly as
 * *"or type a field on the ad…"*. Neither was true: a context root was written onto the ad as
 * `ad.session.…`, and a custom field on the ad is stored where nothing reads it, because `VastAdDto`
 * has no custom home and no reader walks unknown keys. So the box is removed rather than re-worded.
 *
 * Where a custom field extracted from a response lives is `#1205`'s and `#1235`'s to answer. The end
 * state is one autocomplete that accepts a typed custom field and shows the stored spelling at once.
 */
const AddTargetControl: React.FC<{ presentTargets: string[]; onAdd: (t: string) => void }> = ({
  presentTargets,
  onAdd,
}) => {
  return (
    <HStack gap={2}>
      <NativeSelect.Root size="sm" width="auto">
        <NativeSelect.Field
          value=""
          aria-label="Add a context field mapping"
          bg="bg.card"
          color="fg.default"
          onChange={(e) => {
            if (e.target.value) onAdd(e.target.value);
          }}
        >
          <option value="">+ Add field mapping…</option>
          {AD_TARGET_CATALOG.filter((f) => !presentTargets.includes(f.name)).map((f) => (
            <option key={f.name} value={f.name}>
              {f.name}
              {/* **Was `(optimizer)`, and that word now belongs to the mark beside it** (`#1248`).
                  It marked the five `OPTIMIZER_CRITICAL_TARGETS` — *fill these or the optimizer has
                  nothing to work with* — while the derived mark answers *does the optimizer see a
                  value here at all*, which is true of eight. Two claims under one word in one
                  dropdown, and an operator cannot tell them apart: `#1045`'s *never reuse a name the
                  UI already gives to something else*. The word for the first claim is the one the
                  button above it already uses. */}
              {OPTIMIZER_CRITICAL_TARGETS.includes(f.name) ? ' (recommended)' : ''}
              {/* The same mark the type-down carries. This is the *other* way a target is chosen,
                  and a mark on one of two pickers is the shape `#1189` is about. */}
              {targetGrain(f.name).usedByOptimizer ? ` — ${targetGrain(f.name).usedByOptimizer?.label}` : ''}
            </option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
    </HStack>
  );
};
